import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const OccupationalHealth = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">
        How occupational health screening works
      </Typography>
      <Typography>
        Occupational health screening asks every staff member to complete a
        structured health questionnaire — height and weight with a calculated
        BMI, GP details, disabilities, allergies, medications, pregnancy,
        corrective lenses, a medical conditions checklist, immunisations with
        evidence uploads, physical fitness checks, an individual COVID-19 risk
        assessment, and a health &amp; safety declaration. Concerning answers
        are automatically flagged to HR for review.
      </Typography>
      <Typography>
        Screening is part of the{' '}
        <DocLink docsPage url="/employees-hr/appraisals" text="appraisals" />{' '}
        process: the first step of every appraisal shows the staff member a
        summary of their last screening and asks whether anything has changed.
        Confirming creates a fresh dated copy of their answers; saying
        something has changed takes them to the full form to update it. A
        standalone screening (outside an appraisal) is only required from
        staff who have never completed one, or when HR requests a fresh one
        from their profile.
      </Typography>
      <Alert severity="info" variant="outlined">
        Screening is <strong>enabled by default</strong>.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Configuring the screening</Typography>
      <Typography>
        Everything is configured from{' '}
        <DocLink
          url="/site-configuration/occupational-health"
          text="Configuration > Employees > Occupational Health"
        />
        . There is no separate screening frequency — the cadence is linked to
        appraisals — but you choose whether staff may voluntarily recomplete
        their screening at any time rather than only when asked (on by
        default).
      </Typography>
      <Typography>
        Each checklist — medical conditions, immunisations, fitness
        requirements, and COVID-19 risks — is fully editable. Because every
        item on these lists is inherently notable, concerning answers always
        raise a review flag: a condition that is ticked, an immunisation the
        member of staff does <em>not</em> have, a fitness task they cannot
        complete, or a COVID-19 risk they select. You can also add your
        organisation's own Yes/No questions and choose which answer raises a
        flag, exactly like the appraisal question lists.
      </Typography>
      <Typography>
        Three COVID-19 risk answers can be filled in automatically instead of
        being asked: <strong>Aged over 50</strong> (from the employee's date of
        birth), <strong>Currently pregnant</strong> (from the pregnancy
        question earlier in the form), and <strong>BMI over 40</strong> (from
        the calculated BMI). Each has its own toggle, all on by default —
        switch one off and the item becomes a normal tick box for the employee.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The staff member's part</Typography>
      <Typography>
        Staff usually confirm or update their screening as the first step of
        their appraisal on <DocLink url="/my-appraisals" text="My Appraisals" />
        , and can complete the full form at any time from{' '}
        <DocLink url="/my-occupational-health" text="My Occupational Health" />
        . When a standalone screening is required — never completed, or newly
        requested by HR — a prompt also appears in the To Do bar on their home
        screen, and they receive a notification when HR requests one.
      </Typography>
      <Typography>
        Height and weight can be entered in metric or imperial units (cm or
        feet &amp; inches, kg or stones &amp; pounds) and the BMI is calculated
        live as they type. Immunisation evidence — vaccination cards, letters,
        photos, or PDFs — can be uploaded against the submission. The form
        cannot be submitted until every question is answered and the health
        &amp; safety declaration is ticked.
      </Typography>
      <Alert severity="info" variant="outlined">
        If voluntary recompletion is enabled, staff can submit a fresh
        screening whenever their circumstances change — each submission is
        kept as a separate dated record.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reviewing and requesting screenings</Typography>
      <Typography>
        The <strong>Occupational Health</strong> tab on an employee's profile
        shows their current status — complete, never completed, or
        requested — with their full submission history. Opening a submission
        shows every answer, the calculated BMI, uploaded evidence, and any
        flagged items called out at the top. Reviewing requires the{' '}
        <strong>Personnel</strong>, <strong>Manager</strong>, or{' '}
        <strong>Supervisor</strong> team; site administrators always have
        access.
      </Typography>
      <Typography>
        The same tab has a <strong>Request New Screening</strong> button. This
        makes a new screening due immediately regardless of when the last one
        was completed, notifies the employee, and adds the prompt to their To
        Do bar.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Blocking shift bookings</Typography>
      <Typography>
        An option under{' '}
        <DocLink
          url="/site-configuration/shifts"
          text="Configuration > Employees > Shifts"
        />{' '}
        (off by default) prevents staff from applying for advertised shifts
        while their occupational health screening is outstanding — never
        completed, or a new one requested.
        The Available Shifts popup explains why they cannot apply, alongside
        the existing mandatory training and DBS restrictions.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The Occupational Health dashboard</Typography>
      <Typography>
        <DocLink url="/occupational-health" text="People > Occupational Health" />{' '}
        gives reviewers the whole picture in one place. The header shows the
        screening rate across active staff with counts of outstanding
        screenings, flagged staff, open requests and appraisals waiting for a
        screening refresh, plus a{' '}
        <strong>Request from everyone outstanding</strong> button that
        notifies every active employee who has never completed a screening and
        hasn't already been asked.
      </Typography>
      <Typography>
        Below that, charts break down the latest screening per person:
        immunisation coverage per vaccine (lowest first), flags by category,
        BMI distribution, screenings completed per month, the most commonly
        declared conditions and COVID-19 risks, physical fitness pass rates,
        and coverage by clinical role. Every chart has a table view for the
        underlying numbers.
      </Typography>
      <Typography>
        The <strong>staff screening worklist</strong> lists every active
        employee with their status, last screening date, flag count (hover for
        the flags), BMI and evidence uploads. Filter by Outstanding, Flagged,
        Awaiting appraisal refresh or Screened, search by name or role, and
        export to CSV. Each row lets you open the latest submission, request a
        new screening, or jump to the employee's Occupational Health tab.
      </Typography>
      <Alert severity="info" variant="outlined">
        The dashboard needs the <strong>Personnel</strong>,{' '}
        <strong>Manager</strong>, or <strong>Supervisor</strong> team — the
        same access as reviewing a screening on an employee's profile.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Dashboards and KPIs</Typography>
      <Typography>
        The{' '}
        <DocLink
          docsPage
          url="/employees-hr/personnel-dashboard"
          text="Workforce Dashboard"
        />{' '}
        shows an occupational health compliance ring alongside training, DBS,
        and policy acknowledgement, and its Action Centre lists every staff
        member whose screening is outstanding with the reason — both link
        through to the Occupational Health dashboard. The governance
        KPI dashboard's Workforce section includes an{' '}
        <strong>Occupational Health Screening</strong> compliance gauge
        (default target 95%) and an outstanding-screenings count.
      </Typography>
    </Stack>
  </Stack>
)

export default OccupationalHealth
